import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useEffect, useCallback } from "react";
import { btsCaptions, BTS_PHOTO_COUNT } from "@/btsCaptions";

const BackButton = ({ to = "/", testId = "back-link" }) => (
  <Link to={to} className="app-back" data-testid={testId} aria-label="Back">
    <img src="/bts/arrow.png" alt="" className="app-back-img" />
    <span className="sr-only">Back</span>
  </Link>
);

const artworkUrl = "/home/hp-1.jpg";
const workArtworkUrl = "/work/hero.jpg";
const longFormatArtworkUrl = "https://customer-assets-lxgj4vgw.emergentagent.net/job_homepage-builder-99/artifacts/kvne72f1_web%20works%20long%20form.webp";

const longFormatPages = Array.from({ length: 15 }, (_, i) => {
  const n = 15 - i; // start from page 15 and go down to 1
  return `/works-long/p-${String(n).padStart(2, "0")}.jpg`;
});

const navItems = [
  { label: "Work", testId: "portfolio-nav-work-link", className: "nav-work", to: "/work" },
  { label: "Art", testId: "portfolio-nav-art-link", className: "nav-art", to: "/art" },
  { label: "BTS", testId: "portfolio-nav-bts-link", className: "nav-bts", to: "/bts" },
  { label: "Contact", testId: "portfolio-nav-contact-link", className: "nav-contact", to: "/contact" },
];

const Home = () => (
  <main className="portfolio-home" data-testid="portfolio-home-composition">
    <img className="portfolio-artwork desktop-only" src={artworkUrl} alt="Chayati Kaushik, production designer portfolio introduction" data-testid="portfolio-id-badge" />
    <img className="portfolio-artwork mobile-only" src="/mobile/home.jpg" alt="Chayati Kaushik, production designer portfolio introduction" data-testid="portfolio-id-badge-mobile" />
    <nav className="portfolio-nav" aria-label="Portfolio navigation" data-testid="portfolio-navigation">
      {navItems.map((item) => (
        <Link key={item.label} className={`nav-target ${item.className}`} to={item.to} data-testid={item.testId}>
          <span className="nav-label" aria-hidden="true">{item.label}</span>
          <span className="sr-only">Open {item.label}</span>
        </Link>
      ))}
    </nav>
    <p className="sr-only" data-testid="portfolio-description">Production designer working across films, commercials, short films, music videos, spaces, and vibes.</p>
  </main>
);

const Work = () => (
  <main className="work-page" data-testid="work-page">
    <img className="work-artwork desktop-only" src={workArtworkUrl} alt="Choose between long format and short format work" data-testid="work-artwork-desktop" />
    <img className="work-artwork mobile-only" src="/mobile/work.jpg" alt="Choose between long format and short format work" data-testid="work-artwork-mobile" />
    <Link to="/work/long" className="work-hotspot work-hotspot-long" data-testid="work-long-format-link" aria-label="Open long format work">
      <span className="sr-only">Long format</span>
    </Link>
    <Link to="/work/short" className="work-hotspot work-hotspot-short" data-testid="work-short-format-link" aria-label="Open short format work">
      <span className="sr-only">Short format</span>
    </Link>
    <BackButton to="/" testId="work-back-link" />
  </main>
);

const SHORT_IMG_W = 1822;
const SHORT_IMG_H = 1025;
const tabletCoords = [
  { x: 52, y: 328, w: 254, h: 166 },
  { x: 344, y: 328, w: 254, h: 166 },
  { x: 636, y: 328, w: 254, h: 166 },
  { x: 930, y: 328, w: 254, h: 166 },
  { x: 1221, y: 328, w: 255, h: 166 },
  { x: 1513, y: 328, w: 255, h: 166 },
  { x: 52, y: 528, w: 254, h: 166 },
  { x: 344, y: 528, w: 254, h: 166 },
  { x: 636, y: 528, w: 254, h: 166 },
  { x: 930, y: 528, w: 254, h: 166 },
  { x: 1221, y: 528, w: 255, h: 166 },
  { x: 1513, y: 528, w: 255, h: 166 },
  { x: 52, y: 738, w: 254, h: 166 },
  { x: 344, y: 738, w: 254, h: 166 },
  { x: 636, y: 738, w: 254, h: 166 },
  { x: 930, y: 738, w: 254, h: 166 },
  { x: 1221, y: 738, w: 255, h: 166 },
  { x: 1513, y: 738, w: 255, h: 166 },
];
const tabletCoordsPage2 = (() => {
  const rows = [112, 312, 522, 728];
  const cols = [53, 346, 637, 932, 1224, 1515];
  const coords = [];
  for (const y of rows) for (const x of cols) coords.push({ x, y, w: 255, h: 166 });
  return coords;
})();

// Mobile 3-column tablet coords detected from /mobile/shortN.jpg (750x1600 each)
const SHORT_MOBILE_W = 750;
const SHORT_MOBILE_H = 1600;
const mobileColsX = [30, 278, 520];
const mobileColW = 202;
const mobileTabletH = 134;
const buildMobileCoords = (rowYs) => {
  const coords = [];
  for (const y of rowYs) for (const x of mobileColsX) coords.push({ x, y, w: mobileColW, h: mobileTabletH });
  return coords;
};
const mobileCoordsPage1 = buildMobileCoords([400, 568, 734, 896, 1064, 1230]);
const mobileCoordsPage2 = buildMobileCoords([76, 244, 410, 572, 740, 906, 1074, 1232]);
const mobileCoordsPage3 = buildMobileCoords([403, 571, 737, 899, 1067, 1233]);
const shortFormatVideos = [
  "vujU1XconpQ", "Aa6Gf7xscyA", "SFYgxYNgHBI", "mRVgkCTkI5o", "cAtszmWJJYU", "P8SWdNJCsMI",
  "bY5I5BOlQqk", "R0_lukdom1o", "dZh59G6ZwrY", "-T6yWkviuY8", "H-IR8bq1TX4", "FU4LYye8JLA",
  "HqQQBHVF_dM", "UtGDgXbCcek", "1YR7aNiBgX8", "FtahUw8PH14", "O0AeTHrBCB4", "OTn2E5R7C38",
];
const shortFormatVideosPage2 = [
  "vhvkyO2ow_0", "ok-N6Sep2kE", "UXD3ta-E4uk", "l6WNQKvzVQs", "UnmdIWdHsto", "VyeTbORkD_4",
  "yKtHTnyCFL4", "6yr8snFM8Cc", "2NOfhkGNDzM", "Or3tZevJRlY", "LVWps7hAZ74", "coMXj6jx0_s",
  "ZqHm_FsWrnQ", "fXWbfH0oUII", "cs-ScdDsyMQ", "1Jwl_8gPpMo", "uBB4ENdJT98", "w_xMB9yKwW8",
  "6a3IysD6aTo", "yhTTFL26G5U", "ufk_aYUUxZA", "aaRsi6YX-Gc", "nlealsaC-gI", "gYFsEzCZ_kE",
];

const shortFormatVideosPage3 = [
  "xYRw6uRtCf8", "r10HQZpyj1k", "J0xhqOJMEN4", "9fV6ZGxDa6w", "r10HQZpyj1k", "K-PkdHmGB_M",
  "h0R2Fjbu9ws", "0ayV4SHh6YQ", "uNfyuPYM_Oo", "_hQ-l6qQEU0", "yGqKzkVOk4A", "Z9pf6OmsIZM",
  "Kz1zQfzECfs", "6ST7YbX9xwc", "Q8IOMBZryTM", "HzLU8jb_3WY", "I5Scf21Deqc", "QTnYKGctFoU",
];

const shortFormatPages = [
  { bg: "/works-short/page-1.jpg", mobileBg: "/mobile/short1.jpg", coords: tabletCoords, mobileCoords: mobileCoordsPage1, videos: shortFormatVideos, label: "Page 1" },
  { bg: "/works-short/page-2.jpg", mobileBg: "/mobile/short2.jpg", coords: tabletCoordsPage2, mobileCoords: mobileCoordsPage2, videos: shortFormatVideosPage2, label: "Page 2" },
  { bg: "/works-short/page-3.jpg", mobileBg: "/mobile/short3.jpg", coords: tabletCoords, mobileCoords: mobileCoordsPage3, videos: shortFormatVideosPage3, label: "Page 3" },
];

const ShortFormatPage = ({ bg, mobileBg, coords, mobileCoords, videos, indexOffset, label }) => (
  <div className="short-format-frame" data-testid={`short-format-frame-${indexOffset}`}>
    <img className="short-format-bg desktop-only" src={bg} alt={`Commercials, music videos production design work — ${label}`} />
    <img className="short-format-bg mobile-only" src={mobileBg} alt={`Commercials, music videos production design work — ${label}`} />
    <div className="short-format-grid desktop-only" aria-label={`Commercials and music videos — ${label}`}>
      {videos.map((videoId, idx) => {
        const c = coords[idx];
        const style = {
          left: `${(c.x / SHORT_IMG_W) * 100}%`,
          top: `${(c.y / SHORT_IMG_H) * 100}%`,
          width: `${(c.w / SHORT_IMG_W) * 100}%`,
          height: `${(c.h / SHORT_IMG_H) * 100}%`,
        };
        const globalIdx = indexOffset + idx + 1;
        return (
          <a
            key={globalIdx}
            href={`https://youtu.be/${videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="short-format-tablet"
            style={style}
            data-testid={`short-format-tablet-${globalIdx}`}
            aria-label={`Open commercial ${globalIdx} on YouTube`}
          >
            <img
              className="short-format-thumb"
              src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`}
              alt=""
              loading="lazy"
            />
            <span className="short-format-play" aria-hidden="true">
              <svg viewBox="0 0 68 48" width="46" height="32" focusable="false"><path d="M66.52 7.74a8 8 0 0 0-5.63-5.63C55.85.5 34 .5 34 .5s-21.85 0-26.89 1.61A8 8 0 0 0 1.48 7.74 82.5 82.5 0 0 0 0 24a82.5 82.5 0 0 0 1.48 16.26 8 8 0 0 0 5.63 5.63C12.15 47.5 34 47.5 34 47.5s21.85 0 26.89-1.61a8 8 0 0 0 5.63-5.63A82.5 82.5 0 0 0 68 24a82.5 82.5 0 0 0-1.48-16.26z" fill="#ff0000"/><path d="M27 34l17.5-10L27 14z" fill="#fff"/></svg>
            </span>
          </a>
        );
      })}
    </div>
    <div className="short-format-grid mobile-only" aria-label={`Commercials and music videos — ${label}`}>
      {videos.map((videoId, idx) => {
        const c = mobileCoords[idx];
        if (!c) return null;
        const style = {
          left: `${(c.x / SHORT_MOBILE_W) * 100}%`,
          top: `${(c.y / SHORT_MOBILE_H) * 100}%`,
          width: `${(c.w / SHORT_MOBILE_W) * 100}%`,
          height: `${(c.h / SHORT_MOBILE_H) * 100}%`,
        };
        const globalIdx = indexOffset + idx + 1;
        return (
          <a
            key={globalIdx}
            href={`https://youtu.be/${videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="short-format-tablet short-format-tablet-mobile"
            style={style}
            data-testid={`short-format-tablet-mobile-${globalIdx}`}
            aria-label={`Open commercial ${globalIdx} on YouTube`}
          >
            <img
              className="short-format-thumb"
              src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`}
              alt=""
              loading="lazy"
            />
          </a>
        );
      })}
    </div>
  </div>
);

const ShortFormat = () => {
  let offset = 0;
  return (
    <main className="short-format-page" data-testid="short-format-page">
      <BackButton to="/work" testId="short-format-back-link" />
      <div className="short-format-stack">
        {shortFormatPages.map((p, i) => {
          const el = <ShortFormatPage key={p.bg} {...p} indexOffset={offset} />;
          offset += p.videos.length;
          return el;
        })}
      </div>
    </main>
  );
};


const LongFormat = () => (
  <main className="long-format-page" data-testid="long-format-page">
    <BackButton to="/work" testId="long-format-back-link" />
    <div className="long-format-stack" data-testid="long-format-artwork">
      {longFormatPages.map((src, idx) => (
        <img
          key={src}
          className="long-format-page-image"
          src={src}
          alt={`Long format work page ${15 - idx}`}
          loading={idx < 2 ? "eager" : "lazy"}
          data-testid={`long-format-page-${15 - idx}`}
        />
      ))}
    </div>
  </main>
);

const Art = () => (
  <main className="art-page" data-testid="art-page">
    <img className="art-page-image desktop-only" src="/art/p-1.jpg" alt="Art — Work in progress" data-testid="art-page-artwork" />
    <img className="art-page-image mobile-only" src="/mobile/art.jpg" alt="Art — Work in progress" data-testid="art-page-artwork-mobile" />
    <BackButton to="/" testId="art-back-link" />
  </main>
);

// BTS photos are stored under /bts-photos/pNNN.jpg
const btsAllPhotos = Array.from({ length: BTS_PHOTO_COUNT }, (_, i) => `/bts-photos/p${String(i + 1).padStart(3, "0")}.jpg`);

const BTS_IMG_W = 2733;
const BTS_IMG_H = 1537;
// All 18 boxes on the empty BTS cork board are user photos, top-left first, sequential order.
const btsPage1Boxes = (() => {
  const rows = [
    { y: 211, h: 400 },
    { y: 645, h: 400 },
    { y: 1083, h: 401 },
  ];
  const cols = [
    { x: 64, w: 405 }, { x: 505, w: 404 }, { x: 945, w: 404 },
    { x: 1385, w: 404 }, { x: 1825, w: 404 }, { x: 2265, w: 405 },
  ];
  const boxes = [];
  for (const r of rows) for (const c of cols) boxes.push({ x: c.x, y: r.y, w: c.w, h: r.h });
  return boxes;
})();

// Deterministic pseudo-random for tape placement so it does not change between renders
const shouldTape = (idx) => {
  const seed = (idx * 2654435761) >>> 0;
  return (seed % 100) < 60; // ~60% tape rate
};
const tapeVariant = (idx) => ((idx * 2246822519) >>> 0) % 4;

const BtsTape = ({ variant }) => (
  <span className={`bts-tape bts-tape-v${variant}`} aria-hidden="true" />
);

const BtsPhotoCard = ({ src, globalIdx, style }) => {
  const taped = shouldTape(globalIdx);
  const navigate = useNavigate();
  const onOpen = (e) => {
    e.preventDefault();
    navigate(`/bts?photo=${globalIdx}`);
  };
  return (
    <a
      href={`/bts?photo=${globalIdx}`}
      onClick={onOpen}
      className={`bts-photo${taped ? " bts-photo-taped" : ""}`}
      style={style}
      data-testid={`bts-photo-${globalIdx}`}
      aria-label={`Open photo ${globalIdx}${btsCaptions[globalIdx] ? ` — ${btsCaptions[globalIdx]}` : ""}`}
    >
      <img src={src} alt={`Behind the scenes photo ${globalIdx}`} className="bts-photo-image" loading="lazy" />
      {taped && <BtsTape variant={tapeVariant(globalIdx)} />}
    </a>
  );
};

const useBtsLightboxNav = (idx, isOpen) => {
  const [params, setParams] = useSearchParams();
  const close = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("photo");
    setParams(next, { replace: true });
  }, [params, setParams]);
  const go = useCallback((delta) => {
    if (!isOpen) return;
    const wrapped = ((idx - 1 + delta + BTS_PHOTO_COUNT) % BTS_PHOTO_COUNT) + 1;
    const next = new URLSearchParams(params);
    next.set("photo", String(wrapped));
    setParams(next, { replace: true });
  }, [idx, isOpen, params, setParams]);
  return { close, go };
};

const useBtsLightboxKeyboard = (isOpen, close, go) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close, go]);
};

const BtsLightbox = () => {
  const [params] = useSearchParams();
  const raw = params.get("photo");
  const idx = raw ? parseInt(raw, 10) : NaN;
  const isOpen = Number.isInteger(idx) && idx >= 1 && idx <= BTS_PHOTO_COUNT;
  const { close, go } = useBtsLightboxNav(idx, isOpen);
  useBtsLightboxKeyboard(isOpen, close, go);
  if (!isOpen) return null;
  const src = btsAllPhotos[idx - 1];
  const caption = btsCaptions[idx] || "";
  const stopClick = (e) => e.stopPropagation();
  return (
    <div className="bts-lightbox" role="dialog" aria-modal="true" data-testid="bts-lightbox" onClick={close} style={{ backgroundImage: "url('/bts/cork.jpg')" }}>
      <button
        className="bts-lightbox-close"
        onClick={(e) => { stopClick(e); close(); }}
        aria-label="Close photo view"
        data-testid="bts-lightbox-close"
      >×</button>
      <button
        className="bts-lightbox-arrow bts-lightbox-arrow-left"
        onClick={(e) => { stopClick(e); go(-1); }}
        aria-label="Previous photo"
        data-testid="bts-lightbox-prev"
      >
        <img src="/bts/arrow.png" alt="" className="bts-lightbox-arrow-img bts-lightbox-arrow-img-left" />
      </button>
      <div className="bts-lightbox-inner" onClick={stopClick}>
        <img className="bts-lightbox-image" src={src} alt={caption || `Behind the scenes photo ${idx}`} data-testid="bts-lightbox-image" />
        <div className="bts-lightbox-caption" data-testid="bts-lightbox-caption">
          {caption || <span className="bts-lightbox-caption-empty">Photo #{idx} — caption coming soon…</span>}
        </div>
      </div>
      <button
        className="bts-lightbox-arrow bts-lightbox-arrow-right"
        onClick={(e) => { e.stopPropagation(); go(1); }}
        aria-label="Next photo"
        data-testid="bts-lightbox-next"
      >
        <img src="/bts/arrow.png" alt="" className="bts-lightbox-arrow-img" />
      </button>
    </div>
  );
};

const BtsPage1 = () => (
  <section className="bts-frame bts-frame-page1" data-testid="bts-frame-1">
    <img className="bts-page-image" src="/bts/p-1.jpg" alt="Behind the scenes moodboard page 1" />
    {btsPage1Boxes.map((box, idx) => {
      const photoIdx = idx + 1;
      const src = btsAllPhotos[photoIdx - 1];
      const style = {
        left: `${(box.x / BTS_IMG_W) * 100}%`,
        top: `${(box.y / BTS_IMG_H) * 100}%`,
        width: `${(box.w / BTS_IMG_W) * 100}%`,
        height: `${(box.h / BTS_IMG_H) * 100}%`,
      };
      return <BtsPhotoCard key={photoIdx} src={src} globalIdx={photoIdx} style={style} />;
    })}
  </section>
);

const BtsPageGrid = ({ photos, startIdx, pageNo }) => (
  <section className="bts-frame bts-frame-grid" data-testid={`bts-frame-${pageNo}`}>
    <div className="bts-grid">
      {photos.map((src, idx) => (
        <BtsPhotoCard key={startIdx + idx} src={src} globalIdx={startIdx + idx} style={undefined} />
      ))}
    </div>
  </section>
);

const BTS_PAGE1_SLOTS = 18; // 18 user photos p001..p018 on the empty cork template
const BTS_GRID_SLOTS = 18;

const BTS = () => {
  const pages = [];
  let idx = 0;
  // Page 1 uses the PDF background with clickable overlays for all 18 photos
  pages.push({ type: "page1" });
  idx += BTS_PAGE1_SLOTS;
  // Subsequent pages use CSS grid
  let pageNo = 2;
  while (idx < btsAllPhotos.length) {
    const chunk = btsAllPhotos.slice(idx, idx + BTS_GRID_SLOTS);
    pages.push({ type: "grid", photos: chunk, startIdx: idx + 1, pageNo });
    idx += BTS_GRID_SLOTS;
    pageNo += 1;
  }
  return (
    <main className="bts-page" data-testid="bts-page" style={{ backgroundImage: "url('/bts/cork.jpg')" }}>
      <img className="bts-mobile-title mobile-only" src="/mobile/bts-title.png" alt="Behind The Scenes" />
      <BackButton to="/" testId="bts-back-link" />
      <div className="bts-stack">
        {pages.map((p, i) =>
          p.type === "page1" ? (
            <BtsPage1 key="p1" />
          ) : (
            <BtsPageGrid key={`pg-${p.pageNo}`} photos={p.photos} startIdx={p.startIdx} pageNo={p.pageNo} />
          )
        )}
      </div>
      <BtsLightbox />
    </main>
  );
};

const Contact = () => {
  const handleEmailClick = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText("kaushikchayati@gmail.com").then(
        () => toast.success("Copied!", { description: "kaushikchayati@gmail.com" }),
        () => toast("kaushikchayati@gmail.com")
      );
    } else {
      toast("kaushikchayati@gmail.com");
    }
  };
  return (
    <main className="contact-page" data-testid="contact-page">
      <img className="contact-page-image desktop-only" src="/contact/p-1.jpg" alt="Contact Chayati Kaushik" data-testid="contact-page-artwork" />
      <img className="contact-page-image mobile-only" src="/mobile/contact.jpg" alt="Contact Chayati Kaushik" data-testid="contact-page-artwork-mobile" />
      <a
        className="contact-email-hotspot"
        href="mailto:kaushikchayati@gmail.com"
        onClick={handleEmailClick}
        data-testid="contact-email-link"
        aria-label="Email kaushikchayati at gmail dot com"
      >
        <span className="sr-only">kaushikchayati@gmail.com</span>
      </a>
      <Link
        to="/"
        className="contact-bird-hotspot"
        data-testid="contact-bird-home-link"
        aria-label="Back to home"
      >
        <span className="contact-bird-tooltip" aria-hidden="true">back to home</span>
        <span className="sr-only">Back to home</span>
      </Link>
      <BackButton to="/" testId="contact-back-link" />
    </main>
  );
};

const Placeholder = ({ title, testId }) => (
  <main className="placeholder-page" data-testid={testId}>
    <h1 className="placeholder-title">{title}</h1>
    <Link to="/work" className="placeholder-back" data-testid="placeholder-back-link">&larr; Back to Work</Link>
  </main>
);

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors closeButton={false} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/work" element={<Work />} />
        <Route path="/work/long" element={<LongFormat />} />
        <Route path="/work/short" element={<ShortFormat />} />
        <Route path="/art" element={<Art />} />
        <Route path="/bts" element={<BTS />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
